import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { type ReactNode, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const JUMP = 10;

export const pageCount = (total: number, pageSize: number) => Math.max(1, Math.ceil(total / pageSize));

interface PagerProps {
    page: number;
    total: number;
    pageSize: number;
    /** What is being counted, plural: "windows", "events". */
    noun: string;
    onPage: (page: number) => void;
}

/** First / −10 / previous / page of pages / next / +10 / last, then the total and a typed jump. */
export function Pager({ page, total, pageSize, noun, onPage }: PagerProps) {
    const pages = pageCount(total, pageSize);
    const go = (to: number) => onPage(Math.min(pages, Math.max(1, to)));

    return (
        <nav aria-label="Pagination" className="space-y-2 pt-1">
            <div className="flex items-center justify-between gap-1">
                <Step label="First page" disabled={page <= 1} onClick={() => go(1)}>
                    <ChevronsLeft />
                </Step>
                <Step label={`Back ${JUMP} pages`} disabled={page <= 1} onClick={() => go(page - JUMP)}>
                    −{JUMP}
                </Step>
                <Step label="Previous page" disabled={page <= 1} onClick={() => go(page - 1)}>
                    <ChevronLeft />
                </Step>
                <span className="min-w-0 flex-1 text-center text-sm tabular-nums" aria-current="page">
                    {page} / {pages}
                </span>
                <Step label="Next page" disabled={page >= pages} onClick={() => go(page + 1)}>
                    <ChevronRight />
                </Step>
                <Step label={`Forward ${JUMP} pages`} disabled={page >= pages} onClick={() => go(page + JUMP)}>
                    +{JUMP}
                </Step>
                <Step label="Last page" disabled={page >= pages} onClick={() => go(pages)}>
                    <ChevronsRight />
                </Step>
            </div>
            <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground tabular-nums">
                    {total.toLocaleString("en-US")} {noun}
                </span>
                <JumpForm key={page} page={page} pages={pages} onGo={go} />
            </div>
        </nav>
    );
}

function Step({
    label,
    disabled,
    onClick,
    children,
}: {
    label: string;
    disabled: boolean;
    onClick: () => void;
    children: ReactNode;
}) {
    return (
        <Button
            size="sm"
            variant="outline"
            className="px-2.5 tabular-nums"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
        >
            {children}
        </Button>
    );
}

// Keyed by the current page, so the field shows it again after any navigation.
function JumpForm({ page, pages, onGo }: { page: number; pages: number; onGo: (page: number) => void }) {
    const [typed, setTyped] = useState(String(page));
    const target = Number(typed);

    return (
        <form
            className="flex items-center gap-1.5"
            onSubmit={(event) => {
                event.preventDefault();
                if (Number.isInteger(target)) onGo(target);
            }}
        >
            <Input
                type="number"
                inputMode="numeric"
                min={1}
                max={pages}
                aria-label="Page number"
                className="h-9 w-20 md:h-9"
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
            />
            <Button type="submit" size="sm" variant="outline" disabled={!Number.isInteger(target)}>
                Go
            </Button>
        </form>
    );
}
